import * as React from "react";
import { useApolloClient } from "@apollo/client";
import { chakraComponents, MenuPlacement } from "chakra-react-select";
import classNames from "classnames";
import { uniqBy } from "lodash-es";
import {
  AsyncPaginate,
  AsyncPaginateProps,
  LoadOptions,
  withAsyncPaginate
} from "react-select-async-paginate";
import Creatable from "react-select/creatable";
import { generateId, GetPage, setErrorsFromResponse, useField } from "duck";
import { Types as InputsTypes } from "../../../duck";
import InputLayout from "../../input-layout";
import { BASE_CHAKRA_STYLES } from "../../select/duck";
import { constants, Types, utils } from "./duck";
import styles from "./AutoComplete.module.css";

const CreatableAsyncPaginate = withAsyncPaginate(Creatable);

// Menu/MenuList usage breaks `loadOptions` prop.
const { Menu, MenuList, ...components } = chakraComponents;

export interface AutoCompleteProps<
  TOption,
  IsMulti extends boolean = false,
  TValue extends string | number = string
> extends InputsTypes.BaseInputProps {
  getPage: GetPage<TOption, TValue>;
  optionLabel?: Types.GetText<TOption>;
  optionValue?: Types.GetText<TOption>;
  isMulti?: IsMulti;
  controlShouldRenderValue?: boolean;
  value?: IsMulti extends true ? TValue[] : TValue;
  onChange?(
    value: IsMulti extends true ? TValue[] : TValue | null,
    opts: IsMulti extends true
      ? NonNullable<TOption>[]
      : NonNullable<TOption> | null
  ): void;
  isDisabled?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  onBlur?(): void;
  menuPlacement?: MenuPlacement;
  creatable?: boolean;
  className?: string;
  isOptionDisabled?(option: TOption): boolean;
}

const AutoComplete = <
  // eslint-disable-next-line
  TOption extends any = any,
  IsMulti extends boolean = false,
  TValue extends string | number = string
>({
  label,
  labelHidden,
  inputLayoutProps,
  name,
  getPage,
  optionLabel = "name",
  optionValue = "_id",
  isMulti,
  value,
  onChange,
  isDisabled,
  isClearable = true,
  placeholder,
  onBlur,
  controlShouldRenderValue,
  creatable,
  className,
  ...rest
}: AutoCompleteProps<TOption, IsMulti, TValue>): React.ReactElement => {
  const client = useApolloClient();
  const loadOptions: LoadOptions<
    TOption,
    any,
    Types.Additional
  > = React.useCallback(
    (search, _, additional) => {
      const page = additional?.page || 1;
      return getPage(client, {
        search,
        page,
        perPage: constants.PAGE_SIZE
      })
        .then(({ collection, totalCount }) => {
          const totalPages = Math.ceil((totalCount || 1) / constants.PAGE_SIZE);
          return {
            options: (collection || []) as TOption[],
            hasMore: page < totalPages,
            additional: { page: page + 1 }
          };
        })
        .catch(error => {
          setErrorsFromResponse(error);
          return {
            options: [],
            hasMore: false,
            additional: { page: page + 1 }
          };
        });
    },
    [client, getPage]
  );

  const labelId = React.useMemo(() => name || generateId(), [name]);

  const fieldProps = useField(name);

  const loadingRef = React.useRef(false);
  const [valueOptions, setValueOptions] = React.useState<TOption[]>([]);

  const idsOrIdOrNull = value === undefined ? fieldProps.value : value;
  const arrayIds = React.useMemo(
    () =>
      // eslint-disable-next-line
      idsOrIdOrNull === null
        ? []
        : Array.isArray(idsOrIdOrNull)
        ? idsOrIdOrNull
        : [idsOrIdOrNull],
    [idsOrIdOrNull]
  );

  React.useEffect(() => {
    if (loadingRef.current || arrayIds.length === 0) {
      return;
    }

    const missingIds = arrayIds.filter(
      id => !valueOptions.find(item => utils.resolve(item, optionValue) === id)
    );

    if (!missingIds.length || creatable) {
      return;
    }

    loadingRef.current = true;
    getPage(client, {
      page: 1,
      perPage: constants.PAGE_SIZE,
      ids: missingIds
    })
      .then(({ collection }) => {
        setValueOptions(prev =>
          uniqBy([...prev, ...((collection || []) as TOption[])], opt =>
            utils.resolve(opt, optionValue)
          )
        );
      })
      .finally(() => {
        loadingRef.current = false;
      });
  }, [client, getPage, arrayIds, valueOptions, optionValue]);

  const selectValue = arrayIds.map(
    id => valueOptions.find(opt => utils.resolve(opt, optionValue) === id) || id
  );

  return (
    <InputLayout
      id={labelId}
      label={label}
      labelHidden={labelHidden}
      errorMessage={fieldProps.error}
      {...inputLayoutProps}
      input={React.createElement<any>(
        creatable ? CreatableAsyncPaginate : AsyncPaginate,
        {
          value: selectValue,
          loadOptions,
          getOptionValue: (opt: any) =>
            utils.resolve(opt, opt?.__isNew__ ? "value" : optionValue),
          getOptionLabel: (opt: any) =>
            utils.resolve(opt, opt?.__isNew__ ? "label" : optionLabel),
          additional: { page: 1 },
          cacheUniqs: [getPage, isDisabled],
          name: labelId,
          selectRef: ref => {
            if (fieldProps.ref) {
              fieldProps.ref(ref?.inputRef || null);
            }
          },
          className: classNames(styles.autoComplete, className),
          controlShouldRenderValue,
          isClearable: controlShouldRenderValue === false ? false : isClearable,
          isMulti,
          isDisabled,
          placeholder,
          onChange: (opts: any) => {
            const ids = isMulti
              ? (opts as TOption[])?.map((opt: any) =>
                  utils.resolve(opt, opt?.__isNew__ ? "value" : optionValue)
                ) || []
              : (opts as TOption | null)
              ? utils.resolve(opts, opts?.__isNew__ ? "value" : optionValue)
              : null;

            if (onChange) {
              onChange(ids, opts);
            } else if (fieldProps.onChange) {
              fieldProps.onChange(ids);
            }

            /* Concat new options into the cache and do not remove previously selected items to avoid requests */
            if (isMulti) {
              /* Get all cached values */
              const cachedIds = valueOptions.map(opt =>
                utils.resolve(opt, optionValue)
              );

              /* And concat with new ones */
              const missingOpts = opts.filter(
                (opt: any) =>
                  !cachedIds.includes(utils.resolve(opt, optionValue))
              );

              setValueOptions(prev => [...prev, ...missingOpts]);
            } else {
              /* For a single-choice select set the option as it is. */
              setValueOptions(opts ? [opts] : []);
            }
          },
          hideSelectedOptions: false,
          closeMenuOnSelect: !isMulti,
          onBlur: onBlur || fieldProps.onBlur,
          components,
          selectedOptionStyle: "check",
          useBasicStyles: true,
          chakraStyles: {
            ...BASE_CHAKRA_STYLES,
            control: provided => ({
              ...provided,
              ...(!!fieldProps.error && {
                borderColor: "red.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-red-500)",
                _hover: {
                  boxShadow: "0 0 0 1px var(--chakra-colors-red-500)"
                }
              })
            })
          },
          ...rest
        } as AsyncPaginateProps<TOption, any, Types.Additional, IsMulti>
      )}
    />
  );
};

export default AutoComplete;
